import React, { useState, useEffect } from 'react';
import { X, Loader2, Edit3 } from 'lucide-react';
import { notifyError, notifySuccess } from '../../lib/notify';

const API = process.env.REACT_APP_BACKEND_URL || '';

const inputCls =
  'w-full bg-white border border-[#EFE6CF] text-[#0A1F2E] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2CA8A0] focus:ring-2 focus:ring-[#2CA8A0]/25 transition';
const labelCls =
  'font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/60 mb-1.5 block';

const CITY_OPTIONS = [
  'Mexico City', 'Tulum', 'Puerto Vallarta', 'Oaxaca',
  'San Miguel de Allende', 'Mérida', 'Guadalajara', 'Cabo San Lucas',
  'Monterrey', 'Playa del Carmen',
];

// Convert an ISO-8601 UTC string into the `<input type="datetime-local">`
// value the browser expects ("YYYY-MM-DDTHH:mm" in local time).
const isoToLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * iter 142c - admin "Edit before approving" panel for a pending member
 * event submission. PATCHes /api/admin/events/{id} with only the fields the
 * admin actually changed. Status is NOT modified here; the admin still has
 * to click Approve in the moderation queue to publish.
 */
const EditEventModal = ({ open, event, onClose, onSaved }) => {
  const [form, setForm] = useState({
    title: '', summary: '', description: '', category: '', type: 'In-Person',
    location: '', city: '', when_local: '', duration_minutes: 90, url: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !event) return;
    // Canonical "sync prop → form state on modal open" pattern. We genuinely
    // need to mirror the incoming event row's fields into editable inputs
    // each time a new row is opened.
    setForm({
      title: event.title || '',
      summary: event.summary || '',
      description: event.description || '',
      category: event.category || 'Community',
      type: event.type || 'In-Person',
      location: event.location || '',
      city: event.city || '',
      when_local: isoToLocalInput(event.when_utc || ''),
      duration_minutes: event.duration_minutes || 90,
      url: event.url || '',
    });
  }, [open, event]);

  if (!open || !event) return null;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/admin/events/${event.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: form.title,
          summary: form.summary,
          description: form.description,
          category: form.category,
          type: form.type,
          location: form.location,
          city: form.city || undefined,
          when_utc: form.when_local ? new Date(form.when_local).toISOString() : undefined,
          duration_minutes: parseInt(form.duration_minutes, 10) || 90,
          url: form.url || undefined,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.success) {
        notifySuccess('Submission updated. Approve or reject when ready.');
        onSaved?.();
        onClose();
      } else {
        notifyError(d, 'Could not save edits.');
      }
    } catch {
      notifyError('Network error - please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#0A1F2E]/60 backdrop-blur-sm flex items-start md:items-center justify-center overflow-y-auto p-4"
      role="dialog" aria-modal="true"
      data-testid="edit-event-modal"
    >
      <div className="bg-[#F9F6EF] w-full max-w-2xl rounded-2xl overflow-hidden my-8">
        <header className="sticky top-0 bg-[#F9F6EF] border-b border-[#EFE6CF] flex items-center justify-between p-5">
          <div>
            <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#1A5C6E] mb-1">
              Edit submission
            </p>
            <h2 className="font-display italic text-2xl text-[#0A1F2E]">
              {event.submitted_by_name || 'Member event'}
            </h2>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-[#0A1F2E]/55 hover:text-[#0A1F2E]" data-testid="edit-event-close">
            <X className="w-5 h-5" />
          </button>
        </header>

        <form onSubmit={save} className="p-5 space-y-4">
          <div>
            <label className={labelCls}>Title</label>
            <input className={inputCls} value={form.title} onChange={(e) => set('title', e.target.value)} required minLength={4} maxLength={140} data-testid="edit-event-title" />
          </div>
          <div>
            <label className={labelCls}>Summary</label>
            <input className={inputCls} value={form.summary} onChange={(e) => set('summary', e.target.value)} required minLength={10} maxLength={240} data-testid="edit-event-summary" />
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <textarea className={`${inputCls} min-h-[100px]`} value={form.description} onChange={(e) => set('description', e.target.value)} required minLength={20} maxLength={2000} data-testid="edit-event-description" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Category</label>
              <input className={inputCls} value={form.category} onChange={(e) => set('category', e.target.value)} required data-testid="edit-event-category" />
            </div>
            <div>
              <label className={labelCls}>Format</label>
              <select className={inputCls} value={form.type} onChange={(e) => set('type', e.target.value)} data-testid="edit-event-type">
                <option value="In-Person">In-Person</option>
                <option value="Virtual">Virtual</option>
                <option value="Hybrid">Hybrid</option>
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Location</label>
            <input className={inputCls} value={form.location} onChange={(e) => set('location', e.target.value)} required minLength={2} maxLength={180} data-testid="edit-event-location" />
          </div>
          <div>
            <label className={labelCls}>City</label>
            <select className={inputCls} value={form.city} onChange={(e) => set('city', e.target.value)} data-testid="edit-event-city">
              <option value="">No city set</option>
              {CITY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              <option value="Other">Other / Not listed</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>When</label>
              <input type="datetime-local" className={inputCls} value={form.when_local} onChange={(e) => set('when_local', e.target.value)} required data-testid="edit-event-when" />
            </div>
            <div>
              <label className={labelCls}>Duration (min)</label>
              <input type="number" min={15} max={600} className={inputCls} value={form.duration_minutes} onChange={(e) => set('duration_minutes', e.target.value)} required data-testid="edit-event-duration" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Link (optional)</label>
            <input type="url" maxLength={500} className={inputCls} value={form.url} onChange={(e) => set('url', e.target.value)} placeholder="https://" data-testid="edit-event-url" />
          </div>

          {event.peer_circle_group_id && (
            <div className="bg-[#F4ECD8] border border-[#EFE6CF] rounded-lg p-3 text-xs text-[#0A1F2E]/75">
              <strong>Peer-circle scoped</strong> - visible only to members of group <code className="font-mono">{event.peer_circle_group_id}</code> after approval.
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm text-[#0A1F2E]/70 hover:text-[#0A1F2E] font-mono uppercase tracking-[0.12em] text-[11px]" data-testid="edit-event-cancel">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white font-mono uppercase text-[11px] tracking-[0.15em] px-6 py-3 rounded-full transition disabled:opacity-60 inline-flex items-center gap-2" data-testid="edit-event-save">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Edit3 className="w-4 h-4" /> Save edits</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEventModal;
